import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const LOW_BALANCE_THRESHOLD = 3; // days

const PTONotificationsPanel = ({ requests, employees, balances, onApprove, onDeny }) => {
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [denyingId, setDenyingId] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [realtimeEvents, setRealtimeEvents] = useState([]);

  const getEmployee = (id) => employees?.find((e) => e?.id === id);

  // Build notifications from current data
  const buildNotifications = useCallback(() => {
    const notifs = [];

    // 1. Manager alerts: pending PTO requests
    const pending = requests?.filter((r) => r?.status === 'Pending') ?? [];
    pending?.forEach((req) => {
      const emp = getEmployee(req?.employeeId);
      notifs?.push({
        id: `pending-${req?.id}`,
        type: 'manager_approval',
        priority: 'high',
        icon: 'Clock',
        iconColor: 'text-warning',
        iconBg: 'bg-warning/10',
        title: 'PTO Approval Required',
        message: `${emp?.name ?? 'An employee'} requested ${req?.daysRequested} day(s) of ${req?.ptoType} (${req?.startDate} → ${req?.endDate})`,
        requestId: req?.id,
        employeeName: emp?.name,
        ptoType: req?.ptoType,
        startDate: req?.startDate,
        endDate: req?.endDate,
        daysRequested: req?.daysRequested,
        actionable: true,
        timestamp: req?.createdAt,
      });
    });

    // 2. Employee alerts: low PTO balances
    balances?.forEach((bal) => {
      const emp = getEmployee(bal?.employeeId);
      const vacRemaining = parseFloat(bal?.vacationTotal ?? 0) - parseFloat(bal?.vacationUsed ?? 0);
      const sickRemaining = parseFloat(bal?.sickTotal ?? 0) - parseFloat(bal?.sickUsed ?? 0);
      const personalRemaining = parseFloat(bal?.personalTotal ?? 0) - parseFloat(bal?.personalUsed ?? 0);

      if (vacRemaining <= LOW_BALANCE_THRESHOLD && vacRemaining >= 0) {
        notifs?.push({
          id: `low-vac-${bal?.employeeId}`,
          type: 'low_balance',
          priority: vacRemaining === 0 ? 'critical' : 'medium',
          icon: 'AlertTriangle',
          iconColor: vacRemaining === 0 ? 'text-destructive' : 'text-warning',
          iconBg: vacRemaining === 0 ? 'bg-destructive/10' : 'bg-warning/10',
          title: vacRemaining === 0 ? 'Vacation Balance Depleted' : 'Low Vacation Balance',
          message: `${emp?.name ?? 'Employee'} has ${vacRemaining?.toFixed(1)} vacation day(s) remaining`,
          actionable: false,
          timestamp: bal?.updatedAt,
        });
      }

      if (sickRemaining <= LOW_BALANCE_THRESHOLD && sickRemaining >= 0) {
        notifs?.push({
          id: `low-sick-${bal?.employeeId}`,
          type: 'low_balance',
          priority: sickRemaining === 0 ? 'critical' : 'medium',
          icon: 'Thermometer',
          iconColor: sickRemaining === 0 ? 'text-destructive' : 'text-amber-500',
          iconBg: sickRemaining === 0 ? 'bg-destructive/10' : 'bg-amber-50',
          title: sickRemaining === 0 ? 'Sick Balance Depleted' : 'Low Sick Balance',
          message: `${emp?.name ?? 'Employee'} has ${sickRemaining?.toFixed(1)} sick day(s) remaining`,
          actionable: false,
          timestamp: bal?.updatedAt,
        });
      }

      if (personalRemaining <= 1 && personalRemaining >= 0) {
        notifs?.push({
          id: `low-personal-${bal?.employeeId}`,
          type: 'low_balance',
          priority: 'medium',
          icon: 'User',
          iconColor: 'text-violet-500',
          iconBg: 'bg-violet-50',
          title: 'Low Personal Balance',
          message: `${emp?.name ?? 'Employee'} has ${personalRemaining?.toFixed(1)} personal day(s) remaining`,
          actionable: false,
          timestamp: bal?.updatedAt,
        });
      }
    });

    // Sort: critical first, then high, then medium
    const priorityOrder = { critical: 0, high: 1, medium: 2 };
    notifs?.sort((a, b) => (priorityOrder?.[a?.priority] ?? 3) - (priorityOrder?.[b?.priority] ?? 3));

    return notifs;
  }, [requests, balances, employees]);

  useEffect(() => {
    setNotifications(buildNotifications());
  }, [buildNotifications]);

  // Real-time subscription for new PTO requests
  useEffect(() => {
    const channel = supabase
      ?.channel('pto-realtime-notifications')
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'pto_requests',
      }, (payload) => {
        const req = payload?.new;
        if (req?.status === 'Pending') {
          setRealtimeEvents((prev) => [
            {
              id: `rt-${req?.id}-${Date.now()}`,
              message: `New PTO request submitted for approval`,
              timestamp: new Date()?.toISOString(),
            },
            ...prev?.slice(0, 4),
          ]);
        }
      })
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'pto_requests',
      }, (payload) => {
        const req = payload?.new;
        if (req?.status === 'Approved' || req?.status === 'Denied') {
          setRealtimeEvents((prev) => [
            {
              id: `rt-upd-${req?.id}-${Date.now()}`,
              message: `PTO request ${req?.status?.toLowerCase()} — balance updated`,
              timestamp: new Date()?.toISOString(),
            },
            ...prev?.slice(0, 4),
          ]);
        }
      })
      ?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, []);

  const dismiss = (id) => setDismissed((prev) => new Set([...prev, id]));
  const dismissAll = () => setDismissed(new Set(notifications.map((n) => n.id)));

  const visibleNotifs = notifications?.filter((n) => !dismissed?.has(n?.id));
  const pendingApprovals = visibleNotifs?.filter((n) => n?.type === 'manager_approval');
  const lowBalanceAlerts = visibleNotifs?.filter((n) => n?.type === 'low_balance');

  const formatTime = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts)?.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (visibleNotifs?.length === 0 && realtimeEvents?.length === 0) {
    return (
      <div className="bg-card rounded-xl elevation-sm p-6 text-center">
        <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon name="CheckCircle" size={24} className="text-success" />
        </div>
        <p className="text-sm font-body font-medium text-foreground">All caught up!</p>
        <p className="text-xs font-caption text-muted-foreground mt-1">No pending approvals or balance alerts</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Real-time activity feed */}
      {realtimeEvents?.length > 0 && (
        <div className="bg-card rounded-xl elevation-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs font-caption font-semibold text-foreground">Live Activity</span>
          </div>
          <div className="divide-y divide-border/50">
            {realtimeEvents?.map((evt) => (
              <div key={evt?.id} className="px-4 py-2.5 flex items-center gap-3">
                <Icon name="Zap" size={14} className="text-success flex-shrink-0" />
                <p className="text-xs font-body text-foreground flex-1">{evt?.message}</p>
                <span className="text-[11px] font-caption text-muted-foreground whitespace-nowrap">
                  {formatTime(evt?.timestamp)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Manager: Pending Approvals */}
      {pendingApprovals?.length > 0 && (
        <div className="bg-card rounded-xl elevation-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={15} className="text-warning" />
              <span className="text-sm font-heading font-semibold text-foreground">Pending Approvals</span>
              <span className="text-xs font-caption bg-warning/10 text-warning px-2 py-0.5 rounded-full">
                {pendingApprovals?.length}
              </span>
            </div>
          </div>
          <div className="divide-y divide-border/50">
            {pendingApprovals?.map((notif) => (
              <div key={notif?.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg ${notif?.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={notif?.icon} size={16} className={notif?.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-body font-medium text-foreground">{notif?.title}</p>
                      <button
                        onClick={() => dismiss(notif?.id)}
                        className="p-0.5 rounded hover:bg-muted text-muted-foreground flex-shrink-0"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                    <p className="text-xs font-caption text-muted-foreground mt-0.5">{notif?.message}</p>
                    <p className="text-[11px] font-caption text-muted-foreground/60 mt-1">{formatTime(notif?.timestamp)}</p>

                    {/* Action Buttons */}
                    {notif?.actionable && denyingId !== notif?.requestId && (
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => { onApprove?.(notif?.requestId); dismiss(notif?.id); }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-success/10 text-success text-xs font-caption font-medium rounded-lg hover:bg-success/20 transition-colors"
                        >
                          <Icon name="Check" size={12} />
                          Approve
                        </button>
                        <button
                          onClick={() => setDenyingId(notif?.requestId)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-destructive/10 text-destructive text-xs font-caption font-medium rounded-lg hover:bg-destructive/20 transition-colors"
                        >
                          <Icon name="X" size={12} />
                          Deny
                        </button>
                      </div>
                    )}

                    {/* Inline deny form */}
                    {denyingId === notif?.requestId && (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={denyReason}
                          onChange={(e) => setDenyReason(e?.target?.value)}
                          placeholder="Reason for denial..."
                          rows={2}
                          className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-destructive/30 text-foreground placeholder:text-muted-foreground resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              onDeny?.(notif?.requestId, denyReason);
                              setDenyingId(null);
                              setDenyReason('');
                              dismiss(notif?.id);
                            }}
                            className="px-3 py-1 bg-destructive text-destructive-foreground text-xs font-caption rounded-lg hover:bg-destructive/90 transition-colors"
                          >
                            Confirm Deny
                          </button>
                          <button
                            onClick={() => { setDenyingId(null); setDenyReason(''); }}
                            className="px-3 py-1 bg-muted text-muted-foreground text-xs font-caption rounded-lg hover:bg-muted/80 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Employee: Low Balance Alerts */}
      {lowBalanceAlerts?.length > 0 && (
        <div className="bg-card rounded-xl elevation-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={15} className="text-destructive" />
              <span className="text-sm font-heading font-semibold text-foreground">Balance Alerts</span>
              <span className="text-xs font-caption bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                {lowBalanceAlerts?.length}
              </span>
            </div>
            {lowBalanceAlerts?.length > 1 && (
              <button
                onClick={dismissAll}
                className="text-xs font-caption text-muted-foreground hover:text-foreground transition-colors"
              >
                Dismiss all
              </button>
            )}
          </div>
          <div className="divide-y divide-border/50">
            {lowBalanceAlerts?.map((notif) => (
              <div key={notif?.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg ${notif?.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={notif?.icon} size={16} className={notif?.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-body font-medium ${
                        notif?.priority === 'critical' ? 'text-destructive' : 'text-foreground'
                      }`}>
                        {notif?.title}
                      </p>
                      <button
                        onClick={() => dismiss(notif?.id)}
                        className="p-0.5 rounded hover:bg-muted text-muted-foreground flex-shrink-0"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                    <p className="text-xs font-caption text-muted-foreground mt-0.5">{notif?.message}</p>
                    {notif?.priority === 'critical' && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-caption text-destructive">
                        <Icon name="AlertCircle" size={11} />
                        Immediate attention required
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PTONotificationsPanel;
